import {
  IconSearch,
  IconWarning,
  borderRadius,
  colors,
  fontWeights,
} from '@cabonline/ui-shared';
import { FC, ReactNode, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import {
  FormatOptionLabelMeta,
  GroupBase,
  IndicatorsContainerProps,
  InputProps,
  MenuListProps,
  OptionsOrGroups,
  Props,
  default as ReactSelect,
  StylesConfig,
  ThemeConfig,
  ValueContainerProps,
  components,
} from 'react-select';

import { Checkbox } from '../checkbox';
import { StyledRequired } from '../form/form-item';
import { HelpText } from '../input';
import { Label } from '../label';
import { Text } from '../text';
import {
  StyledErrorText,
  StyledErrorWrapper,
  StyledMenuHeader,
  StyledMultiSelectContainer,
  StyledOptionWrapper,
  StyledSearchInput,
  StyledSearchInputWrapper,
  StyledSelectAllButton,
} from './styles';

const getBackgroundColor = (isSelected: boolean, isFocused: boolean) => {
  if (isSelected) {
    if (isFocused) {
      return colors.primaryHover;
    }
    return colors.primary;
  } else if (isFocused) {
    return colors.gray4;
  }
  return colors.white;
};

const getBorderColor = (hasError: boolean) => {
  if (hasError) {
    return colors.red;
  }
  return colors.gray2;
};

const getStyles = <
  Option,
  IsMulti extends boolean = true,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  variant: Variant,
  hasError: boolean
): StylesConfig<Option, IsMulti, Group> => ({
  control: (base, state) => ({
    ...base,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    minHeight: 48,
    transition: 'none',
    boxShadow: state.menuIsOpen ? 'none' : base.boxShadow,
    ...(variant === 'default'
      ? {
          borderColor: getBorderColor(!!hasError),
          ':hover': {
            ...base[':hover'],
            outline: state.isFocused ? `1px solid ${colors.black}` : 'none',
            borderColor: colors.black,
            backgroundColor: colors.gray4,
          },
        }
      : variant === 'filter'
      ? {
          border: 0,
          outline: (state.isFocused && !state.menuIsOpen) ? `2px solid ${colors.black}` : 'none',
        }
      : {}),
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    // Taller than the react-select default so each row is a comfortable touch target.
    padding: '10px 12px',
    backgroundColor: getBackgroundColor(state.isSelected, state.isFocused),
    '&:hover': {
      backgroundColor: state.isSelected ? colors.primaryHover : colors.gray4,
    },
    '&:active': {
      backgroundColor: colors.gray3,
    },
    ...(state.isFocused ? { outline: `2px solid ${colors.black}`, outlineOffset: '-2px' } : {}),
  }),
  placeholder: (base) => ({
    ...base,
    fontWeight: variant === 'filter' ? fontWeights.extraBold : 'inherit',
    color: variant === 'filter' ? colors.black : colors.gray2,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: colors.black,
  }),
  menu: (base) => ({
    ...base,
    width: 'auto',
    minWidth: 'max(100%, 260px)',
    maxWidth: '90vw',
  }),
});

const getThemeConfig = (): ThemeConfig => (theme) => ({
  ...theme,
  borderRadius: borderRadius.s,
  height: 48,
  colors: {
    ...theme.colors,
    primary25: colors.gray4,
    primary: colors.black,
  },
});

const CustomInput = <Option,>(props: InputProps<Option>) => {
  return <components.Input {...props} autoComplete="nope" />;
};

type MultiValueContainerProps = ValueContainerProps<
  OwnOption,
  true,
  GroupBase<OwnOption>
>;

const MultiValueContainer: React.FC<MultiValueContainerProps> = (props) => {
  const selectedValues = props.selectProps.value as unknown[];
  return (
    <components.ValueContainer {...props}>
      {selectedValues && selectedValues.length > 0 && (
        <Text weight="extraBold">
          {props.selectProps.placeholder} ({selectedValues.length})
        </Text>
      )}
      {props.children}
    </components.ValueContainer>
  );
};

interface CustomIndicatorsContainerProps
  extends IndicatorsContainerProps<OwnOption, true, GroupBase<OwnOption>> {
  testID: string;
}

const CustomIndicatorsContainer = (props: CustomIndicatorsContainerProps) => {
  const innerPropsWithTestId = {
    ...props.innerProps,
    'data-testid': props.testID,
  } as React.HTMLProps<HTMLDivElement>;
  return (
    <components.IndicatorsContainer
      {...props}
      innerProps={innerPropsWithTestId}
    />
  );
};

const formatOptionLabel = (
  data: OwnOption,
  formatOptionLabelMeta: FormatOptionLabelMeta<OwnOption>
) => {
  const { label, value } = data;
  const selectedValues = formatOptionLabelMeta.selectValue;
  const isSelected = selectedValues.some((option) => option.value === value);
  const testId = `multi-select-option-${value}`;
  return (
    <StyledOptionWrapper
      data-testid={testId}
      role="option"
      aria-selected={isSelected}
    >
      <Checkbox readOnly checked={isSelected} id={label} />
      <Text size="s" truncate>{label}</Text>
    </StyledOptionWrapper>
  );
};

const NullComponent = () => null;

const formatGroupLabel = (data: GroupBase<OwnOption>) => (
  <Text size="s" color={colors.gray2} weight="bold">
    {data.label}
  </Text>
);

const filterGroupEntry = (
  group: GroupBase<OwnOption>,
  lower: string,
): GroupBase<OwnOption> | null => {
  const filtered = (group.options as OwnOption[]).filter(
    (o) =>
      o.label.toLowerCase().includes(lower) ||
      (group.label ?? '').toLowerCase().includes(lower)
  );
  return filtered.length > 0
    ? ({ ...group, options: filtered } as unknown as GroupBase<OwnOption>)
    : null;
};

const filterFlatEntry = (opt: OwnOption, lower: string): OwnOption | null =>
  opt.label.toLowerCase().includes(lower) ? opt : null;

const filterEntry = (
  opt: OwnOption | GroupBase<OwnOption>,
  lower: string,
): (OwnOption | GroupBase<OwnOption>) | null =>
  'options' in (opt as object)
    ? filterGroupEntry(opt as GroupBase<OwnOption>, lower)
    : filterFlatEntry(opt as OwnOption, lower);

const filterOptions = (
  options: OptionsOrGroups<OwnOption, GroupBase<OwnOption>> | undefined,
  searchTerm: string,
): OptionsOrGroups<OwnOption, GroupBase<OwnOption>> => {
  if (!searchTerm) return options ?? [];
  const lower = searchTerm.toLowerCase();
  return (options ?? [])
    .map((opt) => filterEntry(opt, lower))
    .filter((opt): opt is OwnOption | GroupBase<OwnOption> => opt !== null);
};

const handleSearchKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  onClose: () => void,
): void => {
  if (e.key === 'Escape') {
    onClose();
    return;
  }
  if (e.key === 'Tab') {
    requestAnimationFrame(onClose);
    return;
  }
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
    // Let react-select's own key handling highlight/select options while typing.
    return;
  }
  e.stopPropagation();
};

interface SearchMenuHeaderProps {
  searchPlaceholder: string;
  showSelectAll: boolean;
  allSelected: boolean;
  selectAllLabel: string;
  unselectAllLabel: string;
  onToggleAll: () => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
  onSearchChange: (term: string) => void;
  onClose: () => void;
}

const SearchMenuHeader = ({
  searchPlaceholder,
  showSelectAll,
  allSelected,
  selectAllLabel,
  unselectAllLabel,
  onToggleAll,
  searchInputRef,
  onSearchChange,
  onClose,
}: SearchMenuHeaderProps): React.ReactElement => (
  <StyledMenuHeader>
    <StyledSearchInputWrapper>
      <IconSearch color={colors.gray2} width={18} height={18} />
      <StyledSearchInput
        ref={searchInputRef}
        type="text"
        placeholder={searchPlaceholder}
        defaultValue=""
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={(e) => handleSearchKeyDown(e, onClose)}
      />
    </StyledSearchInputWrapper>
    {showSelectAll && (
      <StyledSelectAllButton
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleAll();
        }}
      >
        {allSelected ? unselectAllLabel : selectAllLabel}
      </StyledSelectAllButton>
    )}
  </StyledMenuHeader>
);

// ─── Pure helpers for useMultiSelectState ────────────────────────────────────

const computeFlatOptions = (options: MultiSelectProps['options']): OwnOption[] =>
  (options ?? []).flatMap((opt) =>
    'options' in (opt as object)
      ? ((opt as GroupBase<OwnOption>).options as OwnOption[])
      : [opt as OwnOption]
  );

const computeAllSelected = (
  flatOptions: OwnOption[],
  value: MultiSelectProps['value'],
): boolean => {
  const selected = (value as OwnOption[] | undefined) ?? [];
  return flatOptions.length > 0 && selected.length === flatOptions.length;
};

const applyToggleAll = (
  allSelected: boolean,
  flatOptions: OwnOption[],
  onChange: MultiSelectProps['onChange'],
): void => {
  if (!onChange) return;
  if (allSelected) {
    onChange([], { action: 'clear', removedValues: flatOptions });
  } else {
    onChange(flatOptions, { action: 'select-option', option: undefined });
  }
};

const resolveMenuList = <T,>(
  searchable: boolean,
  showSelectAll: boolean,
  menuList: T,
): T | undefined => (searchable || showSelectAll ? menuList : undefined);

const useMultiSelectState = ({
  options,
  value,
  onChange,
  testID,
  searchable,
  searchPlaceholder,
  showSelectAll,
  selectAllLabel,
  unselectAllLabel,
  variant,
  hasError,
  menuBodyPrefix,
}: {
  options: MultiSelectProps['options'];
  value: MultiSelectProps['value'];
  onChange: MultiSelectProps['onChange'];
  testID: string;
  searchable: boolean;
  searchPlaceholder: string;
  showSelectAll: boolean;
  selectAllLabel: string;
  unselectAllLabel: string;
  variant: Variant;
  hasError: boolean | string | undefined;
  menuBodyPrefix?: ReactNode;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const flatOptions = useMemo(() => computeFlatOptions(options), [options]);

  const allSelected = useMemo(
    () => computeAllSelected(flatOptions, value),
    [flatOptions, value]
  );

  const filteredOptions = useMemo(
    () => filterOptions(options, searchTerm),
    [options, searchTerm]
  );

  const handleMenuOpen = useCallback(() => setMenuIsOpen(true), []);

  const handleClose = useCallback(() => {
    setMenuIsOpen(false);
    setSearchTerm('');
    if (searchInputRef.current) searchInputRef.current.value = '';
  }, []);

  const handleMenuClose = useCallback(() => {
    requestAnimationFrame(() => {
      if (containerRef.current?.contains(document.activeElement)) return;
      handleClose();
    });
  }, [handleClose]);

  const handleToggleAll = useCallback(() => {
    applyToggleAll(allSelected, flatOptions, onChangeRef.current);
  }, [allSelected, flatOptions]);

  const IndicatorsContainerForReactSelect = useCallback(
    (indicatorProps: IndicatorsContainerProps<OwnOption, true, GroupBase<OwnOption>>) => (
      <CustomIndicatorsContainer {...indicatorProps} testID={testID} />
    ),
    [testID]
  );

  const MenuListForSelect = useCallback(
    (menuListProps: MenuListProps<OwnOption, true, GroupBase<OwnOption>>) => (
      <components.MenuList {...menuListProps}>
        <SearchMenuHeader
          searchPlaceholder={searchPlaceholder}
          showSelectAll={showSelectAll}
          allSelected={allSelected}
          selectAllLabel={selectAllLabel}
          unselectAllLabel={unselectAllLabel}
          onToggleAll={handleToggleAll}
          searchInputRef={searchInputRef}
          onSearchChange={setSearchTerm}
          onClose={handleClose}
        />
        {menuBodyPrefix}
        {menuListProps.children}
      </components.MenuList>
    ),
    [searchPlaceholder, showSelectAll, allSelected, selectAllLabel, unselectAllLabel, handleToggleAll, handleClose, menuBodyPrefix]
  );

  const styles = useMemo(
    () => getStyles<OwnOption, true, GroupBase<OwnOption>>(variant, !!hasError),
    [variant, hasError]
  );

  return {
    containerRef,
    menuIsOpen,
    handleMenuOpen,
    handleMenuClose,
    styles,
    IndicatorsContainerForReactSelect,
    MenuListForSelect: resolveMenuList(searchable, showSelectAll, MenuListForSelect),
    filteredOptions,
  };
};

export interface OwnOption {
  value: string | number;
  label: string;
  __meta?: unknown;
}

type Variant = 'default' | 'filter';

export interface MultiSelectProps
  extends Props<OwnOption, true, GroupBase<OwnOption>> {
  id: string;
  label?: string;
  help?: string;
  hasError?: boolean | string;
  isRequired?: boolean;
  closeMenuOnSelect?: boolean;
  testID?: string;
  variant?: Variant;
  searchable?: boolean;
  searchPlaceholder?: string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  unselectAllLabel?: string;
  menuBodyPrefix?: ReactNode;
}

export const MultiSelect: FC<MultiSelectProps> = (props) => {
  const {
    id,
    label,
    help,
    hasError,
    isRequired,
    options,
    closeMenuOnSelect = false,
    testID = '',
    variant = 'default',
    searchable = false,
    searchPlaceholder = 'Search...',
    showSelectAll = false,
    selectAllLabel = 'Select all',
    unselectAllLabel = 'Unselect all',
    value,
    onChange,
    menuBodyPrefix,
  } = props;

  const {
    containerRef,
    menuIsOpen,
    handleMenuOpen,
    handleMenuClose,
    styles,
    IndicatorsContainerForReactSelect,
    MenuListForSelect,
    filteredOptions,
  } = useMultiSelectState({
    options,
    value,
    onChange,
    testID,
    variant,
    hasError,
    searchable,
    searchPlaceholder,
    showSelectAll,
    selectAllLabel,
    unselectAllLabel,
    menuBodyPrefix,
  });

  return (
    <StyledMultiSelectContainer ref={containerRef} className="react-multi-select-container">
      {label && (
        <Label htmlFor={id}>
          {label}
          {isRequired && <StyledRequired aria-hidden />}
        </Label>
      )}
      {help && <HelpText>{help}</HelpText>}
      {hasError && typeof hasError === 'string' && (
        <StyledErrorWrapper>
          <IconWarning color={colors.red} />
          <StyledErrorText>{hasError}</StyledErrorText>
        </StyledErrorWrapper>
      )}
      <ReactSelect<OwnOption, true, GroupBase<OwnOption>>
        {...props}
        styles={styles}
        theme={getThemeConfig()}
        id={`${id}-input-container`}
        inputId={id}
        isMulti
        isSearchable={false}
        tabSelectsValue={false}
        closeMenuOnSelect={closeMenuOnSelect}
        blurInputOnSelect={closeMenuOnSelect}
        openMenuOnFocus={props.openMenuOnFocus ?? true}
        menuIsOpen={menuIsOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        components={{
          ...props.components,
          Input: CustomInput,
          IndicatorSeparator: NullComponent,
          IndicatorsContainer: IndicatorsContainerForReactSelect,
          ValueContainer: MultiValueContainer,
          MultiValueContainer: NullComponent,
          ...(MenuListForSelect ? { MenuList: MenuListForSelect } : {}),
        }}
        formatOptionLabel={props.formatOptionLabel ?? formatOptionLabel}
        options={searchable ? filteredOptions : options}
        formatGroupLabel={formatGroupLabel}
      />
    </StyledMultiSelectContainer>
  );
};
